import { cn } from "@/lib/utils";
import { useId } from "react";

const GRAIN_TILE_PX = 256;

/** Figma "Grain Low" (NOISE, radius 4) splits luminance into binary dots: the
 *  lower ~51% becomes warm dots, the upper ~51% ink dots — full-density speckle. */
const WARM_DOTS = Array.from({ length: 100 }, (_, i) => (i <= 50 ? 1 : 0)).join(" ");
const INK_DOTS = Array.from({ length: 100 }, (_, i) => (i >= 49 ? 1 : 0)).join(" ");

/**
 * Figma Grain Low as a real SVG in the tree, replicating Figma's own noise-effect
 * export (feTurbulence -> luminanceToAlpha -> discrete threshold -> fixed-opacity
 * floods). A smooth low-alpha noise reads as flat haze; the thresholded dots are
 * what makes the grain visible. CSS mask/background-image variants are dropped by
 * Chromium, so this renders inline instead.
 */
export function GrainLowOverlay({ className }: { className?: string }) {
	const uid = useId().replace(/:/g, "");
	const grainFilter = `${uid}-grain`;
	const tile = `${uid}-tile`;

	return (
		<svg
			aria-hidden="true"
			className={cn("pointer-events-none absolute inset-0 z-0 h-full w-full overflow-hidden", className)}
			preserveAspectRatio="none"
		>
			<defs>
				<filter
					id={grainFilter}
					x="0"
					y="0"
					width={GRAIN_TILE_PX}
					height={GRAIN_TILE_PX}
					filterUnits="userSpaceOnUse"
					colorInterpolationFilters="sRGB"
				>
					<feTurbulence
						type="fractalNoise"
						baseFrequency="0.6667"
						numOctaves="3"
						result="noise"
						seed="3936"
						stitchTiles="stitch"
					/>
					<feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
					<feComponentTransfer in="alphaNoise" result="warmDots">
						<feFuncA type="discrete" tableValues={WARM_DOTS} />
					</feComponentTransfer>
					<feComponentTransfer in="alphaNoise" result="inkDots">
						<feFuncA type="discrete" tableValues={INK_DOTS} />
					</feComponentTransfer>
					<feFlood floodColor="#F1E0CC" floodOpacity="0.08" result="warmFlood" />
					<feComposite in="warmFlood" in2="warmDots" operator="in" result="warm" />
					<feFlood floodColor="#000000" floodOpacity="0.04" result="inkFlood" />
					<feComposite in="inkFlood" in2="inkDots" operator="in" result="ink" />
					<feMerge>
						<feMergeNode in="warm" />
						<feMergeNode in="ink" />
					</feMerge>
				</filter>
				<pattern id={tile} width={GRAIN_TILE_PX} height={GRAIN_TILE_PX} patternUnits="userSpaceOnUse">
					<rect width={GRAIN_TILE_PX} height={GRAIN_TILE_PX} filter={`url(#${grainFilter})`} />
				</pattern>
			</defs>
			<rect width="100%" height="100%" fill={`url(#${tile})`} />
		</svg>
	);
}
